import * as stylex from '@stylexjs/stylex';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

const styles = stylex.create({
    errorContainer: {
        padding: '1.5rem',
        backgroundColor: 'rgba(20, 20, 20, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: '8px',
        margin: '1rem',
        color: '#fff',
        fontFamily: "'Montserrat', sans-serif",
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        textAlign: 'center',
        maxWidth: '500px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    errorHeading: {
        color: 'rgba(255, 120, 50, 0.9)',
        fontSize: '1.5rem',
        marginBottom: '1rem',
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 400,
    },
    errorMessage: {
        fontSize: '0.95rem',
        marginBottom: '1.5rem',
        lineHeight: 1.5,
        color: 'rgba(255, 255, 255, 0.8)',
    },
    retryButton: {
        backgroundColor: 'rgba(255, 120, 50, 0.8)',
        color: '#fff',
        border: 'none',
        padding: '0.5rem 1.2rem',
        borderRadius: '30px',
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 500,
        ':hover': {
            backgroundColor: 'rgba(255, 140, 70, 0.9)',
            transform: 'translateY(-2px)',
        },
    },
    errorDetail: {
        fontSize: '0.8rem',
        marginTop: '1rem',
        padding: '0.8rem',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        color: 'rgba(255, 255, 255, 0.6)',
        maxHeight: '100px',
        overflow: 'auto',
        textAlign: 'left',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        display: 'none',
    },
    showDetails: {
        display: 'block',
    },
});

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    showDetails: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            showDetails: false,
        };
    }

    static getDerivedStateFromError(error: Error) {
        // Update state so the next render shows the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to an error reporting service
        console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    toggleDetails = () => {
        this.setState(prevState => ({
            showDetails: !prevState.showDetails,
        }));
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div {...stylex.props(styles.errorContainer)}>
                    <h2 {...stylex.props(styles.errorHeading)}>🔌 Something went wrong</h2>
                    <p {...stylex.props(styles.errorMessage)}>
                        We're experiencing some technical difficulties with this section.
                        You can try reloading the page or come back later.
                    </p>
                    <button
                        {...stylex.props(styles.retryButton)}
                        onClick={this.handleReset}
                    >
                        Try Again
                    </button>

                    {this.state.error && (
                        <>
                            <div
                                style={{
                                    fontSize: '0.8rem',
                                    marginTop: '1.5rem',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    cursor: 'pointer',
                                }}
                                onClick={this.toggleDetails}
                            >
                                {this.state.showDetails ? 'Hide' : 'Show'} technical details
                            </div>
                            <div {...stylex.props(
                                styles.errorDetail,
                                this.state.showDetails && styles.showDetails
                            )}>
                                {this.state.error.toString()}
                            </div>
                        </>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 